import styled from 'styled-components'
import { PrimaryButton } from 'commonStyledComponents'
import themeAsseticonClosePng from '@theme/icon/close.png'

/** 卡片 + 下方關閉鈕的直向排列 */
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

/**
 * 置中浮動卡片。HintModal 容器本身已是置中透明 overlay，這裡只負責卡片本體。
 *
 * 這一層同時是「保存圖片」的 capture 目標：輸出圖就是整張卡片，含兩顆操作按鈕，
 * 與使用者在畫面上看到的一致。因此背景、留白、圓角都留在這裡，輸出圖才不會是透明底。
 * 唯一排除的是「保存成功」提示（SavedToast 帶 data-html2canvas-ignore）。
 */
export const Card = styled.div`
  width: 7.6rem;
  max-width: calc(100vw - 0.8rem);
  /* 預留下方關閉鈕（0.5rem 間距 + 0.8rem 高） */
  max-height: calc(100vh - 2.9rem);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
  padding: 0.32rem;
  border-radius: 0.28rem;
  background: var(--component-share-video-modal--background);
  color: var(--component-share-video-modal--color);
`

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.24rem;
`

export const BrandIcon = styled.img`
  flex-shrink: 0;
  width: 0.96rem;
  height: 0.96rem;
  border-radius: 0.2rem;
  object-fit: cover;
`

export const HeaderTitle = styled.div`
  flex: 1;
  min-width: 0;
  font-size: 0.32rem;
  line-height: 1.35;
  text-align: left;
  overflow-wrap: anywhere;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`

export const CoverWrapper = styled.div`
  position: relative;
  width: 100%;
  /* 固定 16:9。短視頻傳的是直向 coverphoto_v，由 object-fit: cover 置中裁切，不改卡片版面 */
  aspect-ratio: 16 / 9;
  border-radius: 0.2rem;
  overflow: hidden;
  background: var(--component-share-video-modal--cover-placeholder);
`

export const Cover = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`

/**
 * 封面載入失敗時的可視覆蓋層，點擊可重試；蓋滿 CoverWrapper 以取代殘留的破圖示。
 * 用 <button> 而非 <div>：原本 div + onClick 鍵盤/輔助輸入使用者無法 Tab 聚焦、
 * 也無法用 Enter/Space 觸發（N1-4918 S9 審查發現）。原生 button 自帶鍵盤可聚焦與
 * Enter/Space 觸發 click 的語意，不必再手寫 onKeyDown。
 * position:absolute + inset:0 對 button 一樣生效（left/right/top/bottom 皆非 auto，
 * 用值由此決定，跟 display 是 inline-block 無關），故不需額外設 width/height。
 * 全域 button reset（src/init.css）已清空 border/padding/background/outline，
 * 這裡只需覆寫視覺需要的屬性即可，做法與專案內其它 styled(button) 一致（見 PrimaryButton）。
 */
export const CoverErrorHint = styled.button`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.2rem;
  font-size: 0.26rem;
  text-align: center;
  line-height: 1.4;
  color: var(--component-share-video-modal--toast-color);
  background: var(--component-share-video-modal--cover-placeholder);
  cursor: pointer;

  /* 專案全域有 button reset，會一併清掉預設 outline；鍵盤使用者需要看得見焦點在哪 */
  &:focus-visible {
    outline: 2px solid var(--component-share-video-modal--toast-color);
    outline-offset: -2px;
  }
`

/** 保存完成的提示，浮在封面上（設計稿的「保存成功」） */
export const SavedToast = styled.div`
  position: absolute;
  left: 50%;
  bottom: 0.3rem;
  transform: translateX(-50%);
  padding: 0.12rem 0.32rem;
  border-radius: 2.5rem;
  font-size: 0.28rem;
  white-space: nowrap;
  color: var(--component-share-video-modal--toast-color);
  background: var(--component-share-video-modal--toast-background);
`

export const QrRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
`

/**
 * 分享連結長逾千字元，QR 會落在最高版本（177 模組）。
 * 要掃得到，每個模組至少需要約 2 CSS px，因此 QR 必須佔滿卡片可用寬度。
 */
export const QrBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* 取得可掃描的每模組像素即可，不必佔滿卡片寬度，避免視覺壓迫 */
  max-width: 6rem;
  aspect-ratio: 1;
  padding: 0.14rem;
  border-radius: 0.12rem;
  background: var(--component-share-video-modal--qr-background);

  /*
   * qrcode.react 會依 size prop 自行寫入 inline width/height，
   * 需要 !important 才蓋得過。size 保持大值以取得高解析度，
   * 實際顯示尺寸由容器決定，如此可維持 RWD 且不在 JSX 寫 inline style。
   */
  > canvas {
    width: 100% !important;
    height: auto !important;
  }
`

export const QrText = styled.div`
  font-size: 0.36rem;
  font-weight: bold;
  line-height: 1.4;
  text-align: center;
`

export const ButtonRow = styled.div`
  display: flex;
  gap: 0.24rem;
`

export const SecondaryButton = styled(PrimaryButton)`
  flex: 1;
  min-width: 0;
  background: var(--button-secondary-background);
  color: var(--button-secondary-color);
  font-size: 0.3rem;
  padding: 0.22rem 0.2rem;
`

export const CopyButton = styled(PrimaryButton)`
  flex: 1;
  min-width: 0;
  font-size: 0.3rem;
  padding: 0.22rem 0.2rem;
`

/** 同首頁彈窗廣告（Announcement AdItem）的 BtnCancel */
export const BtnCancel = styled.button`
  margin-top: 0.5rem;
  width: 0.8rem;
  height: 0.8rem;
  background-image: url(${themeAsseticonClosePng});
  background-repeat: no-repeat;
  background-size: cover;
`
